import { Clock, History } from 'lucide-react';
import { useRecoilValue } from 'recoil';

import { sessionState } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { snapshotHistoryState } from '@/state/project';

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const sec = Math.round((Date.now() - then) / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.round(hr / 24)}d ago`;
  } catch {
    return iso;
  }
}

function digestChips(summary: string): string[] {
  return (summary || '')
    .split('·')
    .map((part) => part.trim())
    .filter(Boolean);
}

export default function SnapshotTimeline() {
  const session = useRecoilValue(sessionState);
  const snapshots = useRecoilValue(snapshotHistoryState);

  if (!snapshots?.length) return null;

  const rewind = (messageId: string) => {
    session?.socket?.emit('rewind_request', { message_id: messageId });
  };

  const latestIndex = snapshots.length - 1;

  return (
    <div
      className="flex flex-none items-center gap-2 border-t border-border bg-card/70 px-3 py-1.5"
      role="group"
      aria-label="Operation snapshots"
    >
      <div className="flex flex-none items-center gap-1.5 text-muted-foreground">
        <History className="h-3 w-3" aria-hidden="true" />
        <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide">
          Operation snapshots
        </span>
      </div>
      <div className="custom-scrollbar flex min-w-0 flex-1 items-center overflow-x-auto py-1.5 pl-2">
        <div className="flex items-center">
          {snapshots.map((snapshot, index) => {
            const isLatest = index === latestIndex;
            const chips = digestChips(snapshot.summary);
            return (
              <div
                key={snapshot.message_id}
                className="flex flex-none items-center"
              >
                {index > 0 ? (
                  <div
                    className="h-[2px] w-6 flex-none rounded-full bg-border sm:w-9"
                    aria-hidden="true"
                  />
                ) : null}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'relative z-10 flex h-6 w-6 flex-none items-center justify-center rounded-full border text-[10px] font-semibold tabular-nums',
                        'transition-all duration-200 ease-out hover:scale-110',
                        isLatest
                          ? 'border-primary bg-primary text-primary-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.2)]'
                          : 'border-border bg-card text-muted-foreground hover:border-primary/60 hover:text-primary'
                      )}
                      aria-label={`Snapshot — turn ${snapshot.turn_index + 1} (${formatRelative(
                        snapshot.created_at
                      )})`}
                    >
                      {snapshot.turn_index + 1}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 overflow-hidden p-0">
                    <div className="border-b border-border bg-muted/40 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-foreground">
                          Turn {snapshot.turn_index + 1}
                        </span>
                        {isLatest ? (
                          <span className="rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                            Latest
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        <span>{formatRelative(snapshot.created_at)}</span>
                        <span aria-hidden="true">·</span>
                        <span>{new Date(snapshot.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="px-3 py-2.5">
                      {chips.length ? (
                        <div className="flex flex-wrap gap-1">
                          {chips.map((chip, i) => (
                            <span
                              key={i}
                              className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground"
                            >
                              {chip}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">
                          No recorded state changes.
                        </div>
                      )}
                    </div>
                    <div className="px-3 pb-3">
                      <Button
                        size="sm"
                        className="w-full gap-1.5"
                        onClick={() => rewind(snapshot.message_id)}
                      >
                        <History className="h-3.5 w-3.5" aria-hidden="true" />
                        Rewind to here
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
